import { PageShell } from "@/components/Layout"
import { ProgramForm } from "@/components/Programs/ProgramForm"
import { ProgramsList } from "@/components/Programs/ProgramsList"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { FaPlus } from "react-icons/fa"

import { type MembershipPublic, OrganizationsService } from "@/client"
import useAuth from "@/hooks/useAuth"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/programs/")({
  component: ProgramsPage,
})

function ProgramsPage() {
  const breadcrumbs = useRouteBreadcrumbs("programs.index")
  const { user } = useAuth()
  const { data: memberships } = useQuery<MembershipPublic[]>({
    queryKey: ["myMemberships", user?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: Boolean(user && !user.is_superuser),
    staleTime: 5 * 60 * 1000,
  })

  const isAdmin = Boolean(
    user?.is_superuser ||
      memberships?.some((membership) => membership.role === "admin"),
  )

  const actions = isAdmin ? <CreateProgramDialog /> : undefined

  return (
    <PageShell
      title="Programs"
      description="Manage learning programs and organize related quizzes."
      breadcrumbs={breadcrumbs}
      actions={actions}
      data-testid="programs-page"
    >
      <ProgramsList isAdmin={isAdmin} />
    </PageShell>
  )
}

function CreateProgramDialog() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      size="lg"
    >
      <DialogTrigger asChild>
        <Button data-testid="program-create-button">
          <FaPlus fontSize="16px" />
          Create Program
        </Button>
      </DialogTrigger>
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Program</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <ProgramForm onSuccess={() => setIsOpen(false)} />
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  )
}
